// src/components/panic/PanicTypeOverlayFloating.tsx
import React from "react";
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Avatar,
  Paper,
  Button,
  Stack,
  Fade,
} from "@mui/material";
import {
  DirectionsCar,
  Dialpad,
  Home as HomeIcon,
  Gavel,
  Report,
  Security,
  MedicalServices,
  DriveEta,
  Close as CloseIcon,
} from "@mui/icons-material";
import { useConfirm } from "@/components/dialog/confirm";
import { PANIC_OPTIONS, PanicMask, PanicType } from "@/services/panicTypes";

export type PanicTypeOverlayFloatingProps = {
  open: boolean;
  onClose: () => void;
  /** Called AFTER user confirms in this overlay */
  onSend: (panicType: number) => void;
  /** Bitmask of allowed flags. If 0/undefined/null, all options are shown. */
  allowedMask?: number | null;
  /** Heading text */
  title?: string;
  /** Confirm dialog title (e.g., product name). Defaults to `title`. */
  confirmTitle?: string;
  /** Confirm primary/secondary labels */
  confirmText?: string;
  cancelText?: string;
  /** Optional confirm description override */
  confirmDescription?: string | ((opts: { selectedLabel: string }) => string);
};

const ICONS: Record<number, React.ReactNode> = {
  [PanicType.Road]: <DirectionsCar />,
  [PanicType.USSD]: <Dialpad />,
  [PanicType.Home]: <HomeIcon />,
  [PanicType.Legal]: <Gavel />,
  [PanicType.HiJack]: <Report />,
  [PanicType.HomeInvasion]: <Security />,
  [PanicType.Medical]: <MedicalServices />,
  [PanicType.HomeDrive]: <DriveEta />,
};

const DESCRIPTIONS: Record<number, string> = {
  [PanicType.Road]: "Roadside assist — we’ll send a tow truck to you.",
  [PanicType.USSD]: "Assistance on your mobile via USSD.",
  [PanicType.Home]: "Help with home emergencies and call-outs.",
  [PanicType.Legal]: "Access to legal support and advice.",
  [PanicType.HiJack]: "Immediate response for hijacking incidents.",
  [PanicType.HomeInvasion]: "Rapid escalation for home invasion alerts.",
  [PanicType.Medical]: "Medical emergency response and dispatch.",
  [PanicType.HomeDrive]: "Drive-me-home service when you need it.",
};

const PanicTypeOverlayFloating: React.FC<PanicTypeOverlayFloatingProps> = ({
  open,
  onClose,
  onSend,
  allowedMask,
  title = "Select an Option",
  confirmText = "Send",
  cancelText = "Cancel",
  confirmDescription,
}) => {
  const confirm = useConfirm();

  const showAll = !allowedMask || allowedMask === 0;
  const options = showAll
    ? PANIC_OPTIONS
    : PANIC_OPTIONS.filter((o) => PanicMask.has(allowedMask!, o.value));

  const [selected, setSelected] = React.useState<number | null>(null);

  React.useEffect(() => {
    if (!open) setSelected(null);
  }, [open]);

  const pick = (val: number) => setSelected(val);

  const selectedLabel =
    selected != null
      ? options.find((o) => o.value === selected)?.label ?? ""
      : "";
  const selectedDesc = selected != null ? DESCRIPTIONS[selected] ?? "" : "";

  const handleSend = async () => {
    if (selected == null) return;

    const titleForConfirm = "Are You Sure?";
    const descForConfirm =
      typeof confirmDescription === "function"
        ? confirmDescription({ selectedLabel })
        : confirmDescription ||
          `You want to send a ${selectedLabel} panic?`;

    try {
      await confirm({
        okOnly: false,
        catchOnCancel: false,
        title: titleForConfirm,
        description: descForConfirm,
        confirmText,
        cancelText,
      });
    } catch {
      return; // user cancelled
    }

    onSend(selected);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      keepMounted
      closeAfterTransition
      BackdropProps={{
        sx: {
          backgroundColor: "rgba(0,0,0,0.85)",
          backdropFilter: "blur(10px) saturate(120%)",
          WebkitBackdropFilter: "blur(10px) saturate(120%)",
        },
      }}
    >
      <Box
        sx={{
          position: "fixed",
          inset: 0,
          display: "flex",
          flexDirection: "column",
          p: 2,
          pb: "max(env(safe-area-inset-bottom), 16px)",
          overflow: "hidden",
          color: "common.white",
        }}
      >
        {/* Top-right close (prevents footer overlap issues) */}
        <IconButton
          aria-label="Close"
          onClick={onClose}
          sx={{
            position: "fixed",
            top: 8,
            right: 8,
            color: "common.white",
            zIndex: (t) => t.zIndex.modal + 3,
          }}
        >
          <CloseIcon />
        </IconButton>

        {/* Optional heading (left-aligned near top) */}
        <Typography variant="h6" sx={{ fontWeight: 800, pl: 0.5, pt: 0.5 }}>
          {title}
        </Typography>

        {/* Centered floating icons grid */}
        <Box
          sx={{
            flex: 1,
            display: "grid",
            placeItems: "center",
            overflowY: "auto",
            pt: 1,
            pb: selected ? 16 : 2, // leave room if card is open
          }}
        >
          <Box sx={{ width: "100%", maxWidth: 520, mx: "auto" }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "repeat(3, 1fr)",
                  sm: "repeat(4, 1fr)",
                },
                gap: { xs: 2.5, sm: 3 },
                justifyItems: "center",
                alignItems: "start",
              }}
            >
              {options.map((opt) => (
                <Stack key={opt.value} alignItems="center" spacing={1}>
                  <IconButton
                    onClick={() => pick(opt.value)}
                    size="large"
                    sx={{
                      width: 72,
                      height: 72,
                      borderRadius: "50%",
                      bgcolor:
                        selected === opt.value
                          ? "primary.dark"
                          : "primary.main",
                      color: "primary.contrastText",
                      boxShadow: 3,
                      "&:hover": { bgcolor: "primary.dark" },
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 56,
                        height: 56,
                        bgcolor: "transparent",
                        color: "inherit",
                      }}
                    >
                      {ICONS[opt.value] ?? null}
                    </Avatar>
                  </IconButton>
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 700, letterSpacing: 0.3 }}
                  >
                    {opt.label}
                  </Typography>
                </Stack>
              ))}

              {options.length === 0 && (
                <Typography
                  variant="body2"
                  sx={{
                    gridColumn: "1 / -1",
                    textAlign: "center",
                    opacity: 0.85,
                    mt: 2,
                  }}
                >
                  No panic types are enabled for your profile.
                </Typography>
              )}
            </Box>
          </Box>
        </Box>

        {/* Slide-up detail card */}
        <Fade in={!!selected} unmountOnExit>
          <Paper
            elevation={8}
            sx={{
              position: "fixed",
              left: 16,
              right: 16,
              bottom: 16,
              borderRadius: 2,
              p: { xs: 2, sm: 2.5 },
              bgcolor: "background.paper",
              color: "text.primary",
              zIndex: (t) => t.zIndex.modal + 4, // make sure it's above everything
            }}
          >
            <Stack spacing={1.25}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Avatar
                  sx={{
                    bgcolor: "primary.main",
                    color: "primary.contrastText",
                    width: 40,
                    height: 40,
                  }}
                >
                  {selected != null ? ICONS[selected] : null}
                </Avatar>
                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                  {selectedLabel}
                </Typography>
              </Stack>

              <Typography
                variant="body2"
                sx={{ opacity: 0.9, lineHeight: 1.35, pl: 6 }}
              >
                {selectedDesc}
              </Typography>

              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button onClick={() => setSelected(null)}>{cancelText}</Button>
                <Button
                  onClick={handleSend}
                  variant="contained"
                  disableElevation
                >
                  {confirmText}
                </Button>
              </Stack>
            </Stack>
          </Paper>
        </Fade>
      </Box>
    </Modal>
  );
};

export default PanicTypeOverlayFloating;